/**
 * The grid beside the chart: which columns exist, which are showing, and which get
 * dropped when the window is narrow.
 *
 * Two separate ideas live here and it is worth keeping them apart. *Chosen* is what
 * somebody asked to see and is remembered between visits. *Fitted* is what there is
 * actually room for — the chart is the reason anybody opened this screen, and seven
 * columns beside it left about a finger's width of chart at 1280 wide. A column is
 * never silently forgotten: dropping one for width leaves it chosen, so widening the
 * window brings it back rather than making somebody pick it again.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'

export type Column = {
  key: string
  label: string
  width: number
  /** Dropped first when there is no room. Title never is. */
  optional: boolean
  /** Cannot be turned off: a row without a name is not a row. */
  fixed?: boolean
  /** What the column means, for the chooser. */
  hint?: string
}

/**
 * Every column the grid can show, in the order they appear when chosen.
 *
 * The order is fixed rather than the order they were ticked, because a grid whose
 * columns land in the order somebody happened to pick them is a grid nobody can read
 * twice the same way.
 */
export const COLUMNS: Column[] = [
  { key: 'wbs', label: 'WBS', width: 64, optional: true, hint: 'The outline number — 1, 1.1, 1.2' },
  { key: 'title', label: 'Task', width: 230, optional: false, fixed: true },
  /**
   * "Assigned", not "Owner". A task holds as many people as the work needs — the column can
   * only draw one name and a count, and calling that an owner implied a single person the
   * task model has never had. Hover shows the rest.
   */
  { key: 'assignee', label: 'Assigned', width: 120, optional: true, hint: 'Everybody on it — hover to see them all' },
  { key: 'start', label: 'Start', width: 88, optional: false },
  { key: 'end', label: 'Finish', width: 88, optional: false },
  { key: 'days', label: 'Length', width: 72, optional: false, hint: 'Working days, not calendar days. Type 3d or 24h — changing it changes the hours and the cost.' },
  // Before the per cent on purpose. Both are optional, and the fitting drops the
  // rightmost first — but what a task waits for is the thing somebody came to the grid
  // to set, and how far along it is is already written on the bar.
  { key: 'waits', label: 'Waits for', width: 112, optional: true, hint: 'Predecessors — what this one waits for, and how' },
  { key: 'progress', label: '%', width: 44, optional: true, hint: 'How far along it is' },
  { key: 'feeds', label: 'Feeds', width: 88, optional: true, hint: 'Successors — what is waiting on this' },
  { key: 'rule', label: 'Rule', width: 128, optional: true, hint: 'The constraint on when it can happen' },
  { key: 'deadline', label: 'Deadline', width: 92, optional: true, hint: 'A date promised to somebody' },
  { key: 'slack', label: 'Slack', width: 58, optional: true, hint: 'Days it can slip before the end date moves' },
  { key: 'free_slack', label: 'Free slack', width: 76, optional: true, hint: 'Days it can slip before anything else moves' },
  { key: 'type', label: 'Task type', width: 104, optional: true, hint: 'What stays fixed when the other two change' },
  { key: 'milestone', label: 'Milestone', width: 72, optional: true, hint: 'A point in time, drawn as a diamond' },
  { key: 'done', label: 'Done', width: 52, optional: true, hint: 'Tick it off' },
  { key: 'priority', label: 'Priority', width: 80, optional: true },
  { key: 'work', label: 'Work', width: 66, optional: true, hint: 'Planned hours — the length times the working day, unless somebody typed a figure' },
  { key: 'spent', label: 'Spent', width: 66, optional: true, hint: 'Hours booked against it' },
  { key: 'remaining', label: 'Left', width: 60, optional: true, hint: 'Hours still to go' },
  { key: 'cost', label: 'Cost', width: 96, optional: true, hint: 'Planned hours at the owner\'s rate. A phase totals its tasks.' },
  { key: 'actual_cost', label: 'Spent (R)', width: 96, optional: true, hint: 'Cost of the hours booked so far' },
  { key: 'baseline_start', label: 'Agreed start', width: 96, optional: true, hint: 'From the baseline being measured against' },
  { key: 'baseline_end', label: 'Agreed finish', width: 100, optional: true, hint: 'From the baseline being measured against' },
  { key: 'status', label: 'Status', width: 104, optional: true },
]

/** What a plan screen shows before anybody has an opinion. */
export const DEFAULT_COLUMNS = ['title', 'assignee', 'start', 'end', 'days', 'progress', 'waits']

/** The widest set of columns that still leaves the chart at least this much room. */
export const MIN_CHART = 420

/**
 * The same floor for somebody who went to the chooser and picked.
 *
 * Much lower on purpose. The 420 above protects a person who never asked for columns
 * from a grid that squeezes the chart out; somebody who has just ticked "Cost" has
 * asked, and dropping it on the way to the screen makes the chooser look broken. The
 * floor stays above zero because a chart of no width is not a chart, and this screen
 * scrolls sideways anyway.
 */
export const MIN_CHART_PICKED = 180

const byKey = new Map(COLUMNS.map((c) => [c.key, c]))

/** Whether this is the shipped set, untouched. */
export function isDefault(keys: string[]): boolean {
  return (
    keys.length === DEFAULT_COLUMNS.length && DEFAULT_COLUMNS.every((k) => keys.includes(k))
  )
}

/** The chosen columns as real columns, in catalogue order, title always included. */
export function chosen(keys: string[]): Column[] {
  const wanted = new Set(keys)
  return COLUMNS.filter((c) => c.fixed || wanted.has(c.key))
}

export function columnsFor(available: number, keys: string[] = DEFAULT_COLUMNS): Column[] {
  let columns = chosen(keys)
  const floor = isDefault(keys) ? MIN_CHART : MIN_CHART_PICKED
  const width = (cs: Column[]) => cs.reduce((sum, c) => sum + c.width, 0)
  while (columns.length > 1 && available - width(columns) < floor) {
    const last = [...columns].reverse().find((c) => c.optional)
    if (!last) break
    columns = columns.filter((c) => c !== last)
  }
  return columns
}

/**
 * The columns somebody picked, remembered per screen.
 *
 * Kept in the browser rather than on the server on purpose: which columns you like is
 * about the window you are looking at, not about the plan, and syncing it would make
 * one person's laptop change what another person sees.
 */
export function useColumnChoice(scope: string): {
  keys: string[]
  set: (keys: string[]) => void
  toggle: (key: string) => void
  reset: () => void
} {
  const store = `furi.plan.columns.${scope}`
  const [keys, setKeys] = useState<string[]>(DEFAULT_COLUMNS)

  useEffect(() => {
    try {
      const saved = localStorage.getItem(store)
      const parsed: unknown = saved ? JSON.parse(saved) : null
      if (Array.isArray(parsed)) {
        // Filter against the catalogue: a column that has since been renamed or removed
        // must not leave a saved choice pointing at nothing.
        const live = parsed.filter((k): k is string => typeof k === 'string' && byKey.has(k))
        setKeys(live.length ? live : DEFAULT_COLUMNS)
      } else {
        setKeys(DEFAULT_COLUMNS)
      }
    } catch {
      setKeys(DEFAULT_COLUMNS)
    }
  }, [store])

  const set = useCallback(
    (next: string[]) => {
      setKeys(next)
      try {
        localStorage.setItem(store, JSON.stringify(next))
      } catch {
        // A browser refusing to store a preference is not a reason to refuse the change.
      }
    },
    [store],
  )

  const toggle = useCallback(
    (key: string) => {
      const column = byKey.get(key)
      if (!column || column.fixed) return
      set(keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key])
    },
    [keys, set],
  )

  const reset = useCallback(() => set(DEFAULT_COLUMNS), [set])

  return { keys, set, toggle, reset }
}


/* ------------------------------------------------------------------ the split */

/** The narrowest the grid can be dragged: the name column and a little more. */
export const MIN_GRID = 220
/** The narrowest the chart can be dragged: enough for a fortnight at the usual zoom. */
export const MIN_CHART_PANE = 240

/** How wide every chosen column is, side by side. */
export function gridContentWidth(keys: string[]): number {
  return chosen(keys).reduce((sum, c) => sum + c.width, 0) + 20
}

/**
 * Where the split sits before anybody has dragged it: wide enough for the chosen
 * columns, but never so wide that the chart is a sliver. The 420 is the same floor the
 * old fitting used, kept for the same reason — the chart is what the screen is for.
 */
export function defaultSplit(keys: string[], available: number): number {
  const wanted = gridContentWidth(keys)
  return clampSplit(Math.min(wanted, available - MIN_CHART), available)
}

export function clampSplit(width: number, available: number): number {
  const most = Math.max(MIN_GRID, available - MIN_CHART_PANE)
  return Math.round(Math.max(MIN_GRID, Math.min(width, most)))
}

/**
 * The grid's width, remembered per screen. Null until somebody drags, so the default
 * can keep following the columns they choose; a dragged width is theirs and sticks.
 */
export function useSplit(scope: string): { width: number | null; set: (w: number | null) => void } {
  const store = `furi.plan.split.${scope}`
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    try {
      const saved = localStorage.getItem(store)
      const parsed = saved ? Number(saved) : NaN
      setWidth(Number.isFinite(parsed) && parsed > 0 ? parsed : null)
    } catch {
      setWidth(null)
    }
  }, [store])

  const set = useCallback(
    (next: number | null) => {
      setWidth(next)
      try {
        if (next === null) localStorage.removeItem(store)
        else localStorage.setItem(store, String(next))
      } catch {
        // A browser refusing to remember a width is not a reason to refuse the drag.
      }
    },
    [store],
  )

  return { width, set }
}

/* ------------------------------------------------------------------- the zoom */

/** Pixels per day at 100%. The month view the screen shipped with. */
export const BASE_DAY = 5
/** The zoom steps offered, as percentages, the way every other planner labels them. */
export const ZOOM_STEPS = [40, 60, 100, 150, 200, 300, 400, 600]

export function dayWidthAt(percent: number): number {
  return (BASE_DAY * percent) / 100
}

export function useZoom(scope: string): { percent: number; set: (p: number) => void; step: (dir: 1 | -1) => void } {
  const store = `furi.plan.zoom.${scope}`
  const [percent, setPercent] = useState(100)

  useEffect(() => {
    try {
      const saved = Number(localStorage.getItem(store))
      if (ZOOM_STEPS.includes(saved)) setPercent(saved)
    } catch {
      // Fall back to 100%.
    }
  }, [store])

  const set = useCallback(
    (p: number) => {
      setPercent(p)
      try {
        localStorage.setItem(store, String(p))
      } catch {
        // See above.
      }
    },
    [store],
  )

  const step = useCallback(
    (dir: 1 | -1) => {
      const at = ZOOM_STEPS.indexOf(percent)
      const next = ZOOM_STEPS[Math.max(0, Math.min(ZOOM_STEPS.length - 1, at + dir))]
      if (next !== undefined) set(next)
    },
    [percent, set],
  )

  return { percent, set, step }
}


/* ------------------------------------------------------------- column widths */

/**
 * Widths somebody has dragged, by column key, remembered per screen.
 *
 * Only the columns that were touched are stored, so a column's catalogue width can
 * change in a later release without a stale override pinning the old one on every
 * screen where nobody ever dragged it.
 */
export function useColumnWidths(scope: string): {
  of: (key: string) => number | undefined
  set: (key: string, width: number | null) => void
} {
  const store = `furi.plan.colwidth.${scope}`
  const [widths, setWidths] = useState<Record<string, number>>({})

  useEffect(() => {
    try {
      const saved = localStorage.getItem(store)
      const parsed: unknown = saved ? JSON.parse(saved) : null
      if (parsed && typeof parsed === 'object') {
        const clean: Record<string, number> = {}
        for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
          if (typeof v === 'number' && Number.isFinite(v) && v > 0) clean[k] = v
        }
        setWidths(clean)
      }
    } catch {
      setWidths({})
    }
  }, [store])

  const set = useCallback(
    (key: string, width: number | null) => {
      setWidths((prev) => {
        const next = { ...prev }
        if (width === null) delete next[key]
        else next[key] = width
        try {
          localStorage.setItem(store, JSON.stringify(next))
        } catch {
          // A browser refusing to remember a width is not a reason to refuse the drag.
        }
        return next
      })
    },
    [store],
  )

  return useMemo(() => ({ of: (key: string) => widths[key], set }), [widths, set])
}


/* ------------------------------------------------------------------ density */

/** Row heights, in pixels. Comfortable is the one the screen shipped with. */
export const DENSITIES = { comfortable: 34, compact: 26 } as const
export type Density = keyof typeof DENSITIES

export function useDensity(scope: string): { density: Density; rowHeight: number; toggle: () => void } {
  const store = `furi.plan.density.${scope}`
  const [density, setDensity] = useState<Density>('comfortable')
  useEffect(() => {
    try {
      const saved = localStorage.getItem(store)
      if (saved === 'compact' || saved === 'comfortable') setDensity(saved)
    } catch {
      // Comfortable it is.
    }
  }, [store])
  const toggle = useCallback(() => {
    setDensity((d) => {
      const next: Density = d === 'compact' ? 'comfortable' : 'compact'
      try {
        localStorage.setItem(store, next)
      } catch {
        // Not remembering is not a reason to refuse.
      }
      return next
    })
  }, [store])
  return { density, rowHeight: DENSITIES[density], toggle }
}

/* ------------------------------------------------------------------- filter */

/**
 * Whether a row matches a quick filter, by title, key or owner — case-insensitive,
 * every word must appear somewhere. "smoke june" finds "Smoke test" only if June is in
 * its title, key or owner too, which is what somebody typing two words means.
 */
/**
 * Does this row match what was typed? Every word must, so filters narrow rather than widen.
 *
 * THE KEY IS NOT SEARCHED AS ORDINARY TEXT, AND THAT IS THE WHOLE POINT OF THIS FUNCTION.
 *
 * It used to be, and the result was a filter that looked broken. Every key in a project shares
 * the project's code — `GOLD-PORTAL-1`, `GOLD-PORTAL-19`, and so on — so on a project called
 * Client portal, typing `portal` matched all sixteen rows through their keys and the plan
 * appeared not to filter at all. The one word most worth typing on that project was the one
 * word that could not narrow it.
 *
 * So a word only reaches the key IF IT CONTAINS A DIGIT. That is the shape of a key reference
 * and nothing else: `19`, `portal-19`, `GOLD-PORTAL-19` all find the task, while `portal`,
 * `gold` and any other bare word are read as what somebody meant — words in the title, or a
 * person's name.
 */
export function matchesFilter(
  row: { title: string; key: string; assignee: string | null },
  query: string,
): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (!words.length) return true
  const text = `${row.title} ${row.assignee ?? ''}`.toLowerCase()
  const key = row.key.toLowerCase()
  return words.every((w) => text.includes(w) || (/\d/.test(w) && key.includes(w)))
}
